import React, { useContext } from 'react';
import cn from 'clsx';
import { messageHasReactions, useMessageContext } from 'stream-chat-react';
import { makeStyles } from '@material-ui/styles';
import { UserBadge } from '@bizzabo/collaboration-common';
import { EyeOffLine, PinLineFill, Text, Theme } from '@bizzabo/design-system';
import { useUserBusinessCardAtom, useProfileIdByChatId } from '@bizzabo/entities';
import ReplyAvatarList from '../ReplyAvatarList';
import CustomReactionsList from '../CustomReactionsList';
import MessageWrapper from '../MessageWrapper';
import ReactionsPopover from '../ReactionsPopover';
import { customReactions } from '../../utils/reactions';
import { isUserModeratorOnThisChannel } from '../../utils/userUtils';
import { IChatChannel } from '../../interfaces/IChatChannel';
import SessionChatCtx from '../../context/SessionChatCtx';
import MessageActions from './Actions/MessageActions';
import {
    isDeletedMessage,
    isErrorMessage,
    isMessageFailed,
    isMessageReply,
    isShadowMessageForOtherUser,
} from './Utils/MessageUtils';
import MessageDeleted from './MessageDeleted';
import ReplyButton from '../ReplyButton';
import MessageTextContainer from './MessageTextContainer';
import MessageFailed from './MessageFailed';
import UserBadgeNameSibling from './Components/UserBadgeNameSibling';
import { UserRole } from '../../types/UserRole';
import useTypeTracking from '../../analytics';

interface ICustomMessageProps {
    chatChannel: IChatChannel;
    userRole: UserRole;
    onTogglePopover?: (value: boolean) => void;
}

const CustomMessage: React.FC<ICustomMessageProps> = ({
    chatChannel,
    userRole,
    onTogglePopover,
}) => {
    const classes = useStyles();
    const { title = '', channel } = chatChannel;
    const sessionChatCtx = useContext(SessionChatCtx);
    const {
        message,
        showDetailedReactions,
        threadList,
        initialMessage,
        handleReaction,
        isMyMessage,
    } = useMessageContext();
    const { Track, trackClickEvent } = useTypeTracking({
        data: {
            description: {
                channel_type: 'SESSION_CHAT',
                channel_id: channel.id,
                channel_title: title,
                message_type: 'TEXT',
                message_id: message.id,
                parent_type: message?.parent_id && 'TEXT',
                parent_id: message?.parent_id,
                is_pinned: message.pinned,
            },
        },
    });
    const reactionCounts = message.reaction_counts || {};
    const messageUniqueReactions = Object.keys(reactionCounts).length;
    const isAllReactionSelected = customReactions.length === messageUniqueReactions;

    const hasReactions = messageHasReactions(message);
    const errorMessage = isErrorMessage(message);
    const messageDeleted = isDeletedMessage(message);
    const messageFailed = isMessageFailed(message);
    const messageReply = isMessageReply(message);

    const currentUser = channel.getClient().user;
    const messageShadowedFromOtherUser = isShadowMessageForOtherUser(message, currentUser);

    const ownReactionTypes = message?.own_reactions?.map(r => r.type);
    const { openChatIdPopup } = useUserBusinessCardAtom();

    const isMyReaction = (reactionType: string): boolean => {
        return ownReactionTypes?.some(type => type === reactionType);
    };

    const isUserModerator = userRole === UserRole.Moderator;
    const isMessageUserModerator = isUserModeratorOnThisChannel(channel, message.user);

    const handleReactionClicked = async (reactionType, event) => {
        onTogglePopover?.(false);
        const isMyOwnReaction = isMyReaction(reactionType);
        handleReaction(reactionType, event);
        if (!isMyOwnReaction) {
            trackClickEvent({
                buttonType: title,
                subCategory: 'EMOJI_REACTION',
                description: { emoji_type: reactionType },
            });
        }
    };

    const { refetch } = useProfileIdByChatId({
        chatId: message?.user?.id,
        eventId: sessionChatCtx?.event.id,
        options: {
            refetchOnWindowFocus: false,
            enabled: false,
        },
    });

    const showUserPopupHandler = async props => {
        openChatIdPopup({
            ...props,
            isModerator: isUserModeratorOnThisChannel(channel, message.user),
        });

        if (sessionChatCtx?.event.id) {
            try {
                const { data: userProfileId } = await refetch();
                if (!userProfileId) {
                    return;
                }

                trackClickEvent({
                    buttonType: 'SESSION_ENGAGEMENT_PANEL',
                    subCategory: 'PROFILE_CLICK',
                    description: { attendee_id: userProfileId },
                });
            } catch (error) {
                console.error(error);
            }
        }
    };

    if (messageDeleted) {
        return (
            <MessageWrapper>
                <div className={classes.messageContainer}>
                    <MessageDeleted title={title} />
                </div>
            </MessageWrapper>
        );
    }

    if (messageFailed) {
        return (
            <MessageWrapper>
                <div className={classes.messageContainer}>
                    <MessageFailed showUserPopup={showUserPopupHandler} />
                </div>
            </MessageWrapper>
        );
    }

    if (messageShadowedFromOtherUser) {
        return (
            <MessageWrapper>
                <div className={classes.shadowMessageContainer}>
                    <div />
                </div>
            </MessageWrapper>
        );
    }

    return (
        <Track>
            <MessageWrapper>
                <div
                    className={cn(classes.messageContainer, {
                        [classes.firstMessageThread]: threadList && initialMessage,
                        'is-reply': messageReply,
                    })}
                >
                    {errorMessage && (
                        <div className={classes.visibleText}>
                            <EyeOffLine className={classes.eyeIcon} width={16} height={16} />
                            <Text variant="t70" className={classes.visibleForYou}>
                                This is only visible to you
                            </Text>
                        </div>
                    )}
                    {message.pinned && (
                        <div className={classes.pinned}>
                            <PinLineFill />
                            <Text variant="t80">Pinned conversation</Text>
                        </div>
                    )}
                    <div className={classes.userContainer}>
                        <UserBadge
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            user={message.user}
                            showUserPopup={showUserPopupHandler}
                            nameSibling={
                                <UserBadgeNameSibling channel={channel} message={message} />
                            }
                            nameSiblingWithWrap={isMessageUserModerator}
                            isBadgeClickable={!isMyMessage()}
                            className="user-badge-inside-message"
                        />
                        {isUserModerator && <MessageActions />}
                    </div>
                    <div className={classes.contentWrapper}>
                        {errorMessage && (
                            <div className={classes.customErrorText}>
                                This message was blocked due to suspicion of inappropriate content
                            </div>
                        )}

                        {!errorMessage && <MessageTextContainer />}
                        <div className={classes.userInteractions}>
                            {!threadList && !errorMessage && (
                                <div className={classes.replies}>
                                    {message.reply_count > 0 && <ReplyAvatarList />}
                                    <ReplyButton title={title} />
                                </div>
                            )}
                            <div className={classes.row}>
                                <div className={classes.reactionsListContainer}>
                                    {!isAllReactionSelected && !errorMessage && (
                                        <ReactionsPopover
                                            onTogglePopover={onTogglePopover}
                                            onReactionClick={handleReactionClicked}
                                        />
                                    )}

                                    {hasReactions && !showDetailedReactions && (
                                        <CustomReactionsList
                                            onReactionClick={handleReactionClicked}
                                            reactionOptions={customReactions}
                                            reactionCounts={message.reaction_counts}
                                            ownReactionTypes={ownReactionTypes}
                                            channel={channel}
                                            message={message}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </MessageWrapper>
        </Track>
    );
};

const useStyles = makeStyles(
    (theme: Theme) => ({
        shadowMessageContainer: {
            position: 'relative',
            padding: `1px`,
        },
        messageContainer: {
            position: 'relative',
            padding: `12px 28px`,
            '&:after': {
                right: '28px',
                left: '28px',
                content: '""',
                position: 'absolute',
                bottom: 0,
                height: 1,
                backgroundColor: theme.palette.greys[300],
            },
            '&.is-reply': {
                padding: `12px 34px`,
                color: 'red',
                '&::before': {
                    content: '""',
                    left: 20,
                    width: 4,
                    height: '85%',
                    display: 'block',
                    position: 'absolute',
                    backgroundColor: theme.palette.greys[300],
                },
            },
        },
        contentWrapper: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        userContainer: {
            display: 'flex',

            '& .user-badge-inside-message': {
                width: 'calc(100% - 30px)',
            },
        },
        firstMessageThread: {
            '&:after': {
                display: 'none',
            },
        },
        visibleText: {
            alignItems: 'center',
            paddingBottom: 4,
            color: theme.palette.greys[500],
        },
        customErrorText: {
            ...theme.typography.t71,
            wordBreak: 'break-word',
            color: theme.palette.primary[500],
        },
        row: {
            display: 'flex',
            marginLeft: 'auto',
            paddingBottom: 4,
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
        },
        reactionsListContainer: {
            display: 'flex',
            flex: 1,
            justifyContent: 'flex-end',
        },
        replies: {
            display: 'flex',
            alignItems: 'center',
        },
        eyeIcon: {
            marginRight: 4,
        },
        visibleForYou: {
            fontWeight: 400,
            fontSize: 12,
        },
        arrowIcon: {
            fill: theme.palette.greys[600],
            height: 14,
            width: 14,
        },
        userInteractions: {
            display: 'flex',
            flex: 1,
            flexWrap: 'wrap-reverse',
            alignItems: 'flex-end',
            alignSelf: 'center',
        },
        pinned: {
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.accent[600],
            paddingBottom: 4,
            '& svg': {
                width: 16,
                height: 16,
                marginRight: 5,
            },
        },
    }),
    { classNamePrefix: CustomMessage.name }
);

export default CustomMessage;
